import { dateStringToTimestamp, isoToTimestamp } from './timestamp';

const { PaginationRequest } = require('./proto/acompanha/common/v1/common_pb');

function paginationFromQuery(query) {
    const pagination = new PaginationRequest();
    const page = parseInt(query.page, 10);
    const limit = parseInt(query.limit, 10);
    if (page > 0) {
        pagination.setPage(page);
    }
    if (limit > 0) {
        pagination.setLimit(limit);
    }
    if (query.q) {
        pagination.setQ(String(query.q));
    }
    return pagination;
}

function optionalIsoTimestamp(value) {
    if (!value) {
        return null;
    }
    const trimmed = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        return dateStringToTimestamp(trimmed);
    }
    return isoToTimestamp(trimmed);
}

function customerRefFromRest(data) {
    const { CustomerRef } = require('./proto/acompanha/common/v1/common_pb');
    if (!data || typeof data !== 'object') {
        return null;
    }
    const ref = new CustomerRef();
    const id = data.id || data.uuid || data.customer_uuid;
    if (id) {
        ref.setId(String(id));
    }
    if (data.name) {
        ref.setName(String(data.name));
    }
    if (data.phone) {
        ref.setPhone(String(data.phone));
    }
    if (data.email) {
        ref.setEmail(String(data.email));
    }
    if (data.document) {
        ref.setDocument(String(data.document));
    }
    return ref;
}

function salesChannelRefFromRest(data) {
    const { SalesChannelRef } = require('./proto/acompanha/common/v1/common_pb');
    if (!data || typeof data !== 'object') {
        return null;
    }
    const id = data.id || data.uuid;
    const name = data.name;
    if (!id && !name) {
        return null;
    }
    const ref = new SalesChannelRef();
    if (name) {
        ref.setName(String(name));
    }
    if (id) {
        ref.setId(String(id));
    }
    return ref;
}

function shippingMethodRefFromRest(data) {
    const { ShippingMethodRef } = require('./proto/acompanha/common/v1/common_pb');
    if (!data || typeof data !== 'object') {
        return null;
    }
    const ref = new ShippingMethodRef();
    if (data.name) {
        ref.setName(String(data.name));
    }
    if (data.cost != null) {
        ref.setCost(Number(data.cost));
    }
    if (data.delivery_days != null) {
        ref.setDeliveryDays(Number(data.delivery_days));
    }
    if (data.deadline) {
        ref.setDeadline(String(data.deadline));
    }
    return ref;
}

function checkoutProductFromRest(data) {
    const { CheckoutProduct } = require('./proto/acompanha/common/v1/common_pb');
    if (!data || typeof data !== 'object') {
        return null;
    }
    const p = new CheckoutProduct();
    const id = data.id || data.uuid || data.product_uuid;
    if (id) {
        p.setId(String(id));
    }
    if (data.name) {
        p.setName(String(data.name));
    }
    if (data.quantity != null) {
        p.setQuantity(Number(data.quantity));
    }
    if (data.base_price != null) {
        p.setBasePrice(Number(data.base_price));
    }
    if (data.cost_price != null) {
        p.setCostPrice(Number(data.cost_price));
    }
    return p;
}

function paymentMethodFromRest(data) {
    const { PaymentMethod } = require('./proto/acompanha/common/v1/common_pb');
    if (!data || typeof data !== 'object') {
        return null;
    }
    const p = new PaymentMethod();
    if (data.name) {
        p.setName(String(data.name));
    }
    if (data.installments != null) {
        p.setInstallments(Number(data.installments));
    }
    if (data.amount != null) {
        p.setAmount(Number(data.amount));
    }
    const ts = optionalIsoTimestamp(data.payment_date);
    if (ts) {
        p.setPaymentDate(ts);
    }
    return p;
}

function paymentDetailsFromRest(data) {
    const { PaymentDetails } = require('./proto/acompanha/common/v1/common_pb');
    if (!data || typeof data !== 'object') {
        return null;
    }
    const p = new PaymentDetails();
    if (data.paid != null) {
        p.setPaid(Boolean(data.paid));
    }
    const ts = optionalIsoTimestamp(data.payment_date);
    if (ts) {
        p.setPaymentDate(ts);
    }
    return p;
}

function jobFilamentsFromRest(filaments) {
    const { JobFilament } = require('./proto/acompanha/jobs/v1/jobs_pb');
    if (!Array.isArray(filaments)) {
        return [];
    }
    return filaments
        .filter((f) => f && (f.color || '').trim())
        .map((f) => {
            const row = new JobFilament();
            row.setColor(String(f.color).trim());
            return row;
        });
}

function metadataToJson(metadata) {
    if (metadata == null) {
        return '';
    }
    if (typeof metadata === 'string') {
        return metadata;
    }
    try {
        return JSON.stringify(metadata);
    } catch {
        return '';
    }
}

function measuresToJson(measures) {
    if (measures == null) {
        return '';
    }
    if (typeof measures === 'string') {
        return measures;
    }
    try {
        return JSON.stringify(measures);
    } catch {
        return '';
    }
}

function replacementPartsFromRest(parts) {
    const { ReplacementPart } = require('./proto/acompanha/printer_maintenances/v1/printer_maintenances_pb');
    if (!Array.isArray(parts)) {
        return [];
    }
    return parts
        .map((part) => {
            if (typeof part === 'string' && part.trim()) {
                const row = new ReplacementPart();
                row.setName(part.trim());
                return row;
            }
            if (part?.name) {
                const row = new ReplacementPart();
                row.setName(String(part.name).trim());
                return row;
            }
            return null;
        })
        .filter(Boolean);
}

export function buildPaginationRequest(query) {
    return paginationFromQuery(query || {});
}

export function buildLoginRequest(data) {
    const { LoginRequest } = require('./proto/acompanha/auth/v1/auth_pb');
    const req = new LoginRequest();
    req.setEmail(String(data?.email || '').trim());
    req.setPassword(String(data?.password || ''));
    return req;
}

export function buildRegisterRequest(data) {
    const { RegisterRequest } = require('./proto/acompanha/auth/v1/auth_pb');
    const req = new RegisterRequest();
    req.setName(String(data?.name || '').trim());
    req.setEmail(String(data?.email || '').trim());
    req.setPassword(String(data?.password || ''));
    req.setStoreName(String(data?.store_name || '').trim());
    return req;
}

export function buildUpdateProfileRequest(data) {
    const { UpdateProfileRequest } = require('./proto/acompanha/account/v1/account_pb');
    const req = new UpdateProfileRequest();
    if (data?.name != null) {
        req.setName(String(data.name));
    }
    if (data?.email != null) {
        req.setEmail(String(data.email));
    }
    if (data?.whatsapp != null) {
        req.setWhatsapp(String(data.whatsapp));
    }
    if (data?.site != null) {
        req.setSite(String(data.site));
    }
    if (data?.instagram != null) {
        req.setInstagram(String(data.instagram));
    }
    if (data?.tiktok != null) {
        req.setTiktok(String(data.tiktok));
    }
    if (data?.store_name != null) {
        req.setStoreName(String(data.store_name));
    }
    return req;
}

export function buildUpdatePasswordRequest(data) {
    const { UpdatePasswordRequest } = require('./proto/acompanha/account/v1/account_pb');
    const req = new UpdatePasswordRequest();
    req.setCurrentPassword(String(data?.current_password || ''));
    req.setNewPassword(String(data?.new_password || ''));
    return req;
}

export function buildUpdateAvatarRequest(data) {
    const { UpdateAvatarRequest } = require('./proto/acompanha/account/v1/account_pb');
    const req = new UpdateAvatarRequest();
    req.setImage(String(data?.image || ''));
    return req;
}

export function buildListJobsRequest(query) {
    const { ListJobsRequest } = require('./proto/acompanha/jobs/v1/jobs_pb');
    const req = new ListJobsRequest();
    req.setPagination(buildPaginationRequest(query));
    if (query.printer_uuid) {
        req.setPrinterUuid(String(query.printer_uuid));
    }
    if (query.status) {
        req.setStatus(String(query.status));
    }
    const from = optionalIsoTimestamp(query.finished_from);
    const to = optionalIsoTimestamp(query.finished_to);
    if (from) {
        req.setFinishedFrom(from);
    }
    if (to) {
        req.setFinishedTo(to);
    }
    return req;
}

export function buildPrinterStatsRequest(query) {
    const { PrinterStatsRequest } = require('./proto/acompanha/jobs/v1/jobs_pb');
    const req = new PrinterStatsRequest();
    if (query.q) {
        req.setQ(String(query.q));
    }
    if (query.printer_uuid) {
        req.setPrinterUuid(String(query.printer_uuid));
    }
    if (query.status) {
        req.setStatus(String(query.status));
    }
    const from = optionalIsoTimestamp(query.finished_from);
    const to = optionalIsoTimestamp(query.finished_to);
    if (from) {
        req.setFinishedFrom(from);
    }
    if (to) {
        req.setFinishedTo(to);
    }
    return req;
}

function fillJobMutation(req, data) {
    const d = data || {};
    req.setPrinterUuid(String(d.printer_uuid || ''));
    req.setFileName(String(d.file_name || ''));
    if (d.job_id) {
        req.setJobId(String(d.job_id));
    }
    req.setEstimatedDurationMinutes(Number(d.estimated_duration_minutes) || 0);
    req.setEstimatedFilamentConsumptionGrams(Number(d.estimated_filament_consumption_grams) || 0);
    const started = optionalIsoTimestamp(d.started_at);
    const finished = optionalIsoTimestamp(d.finished_at);
    if (started) {
        req.setStartedAt(started);
    }
    if (finished) {
        req.setFinishedAt(finished);
    }
    if (d.status) {
        req.setStatus(String(d.status));
    }
    if (d.status_reason) {
        req.setStatusReason(String(d.status_reason));
    }
    req.setObjectCount(Number(d.object_count) || 0);
    req.setPrintedCount(Number(d.printed_count) || 0);
    req.setFilamentsList(jobFilamentsFromRest(d.filaments));
    const meta = metadataToJson(d.metadata);
    if (meta) {
        req.setMetadataJson(meta);
    }
}

export function buildCreateJobRequest(data) {
    const { CreateJobRequest } = require('./proto/acompanha/jobs/v1/jobs_pb');
    const req = new CreateJobRequest();
    fillJobMutation(req, data);
    return req;
}

export function buildUpdateJobRequest(uuid, data) {
    const { UpdateJobRequest } = require('./proto/acompanha/jobs/v1/jobs_pb');
    const req = new UpdateJobRequest();
    req.setUuid(String(uuid));
    fillJobMutation(req, data);
    return req;
}

function fillPrinterMutation(req, data) {
    const d = data || {};
    req.setName(String(d.name || ''));
    req.setBrand(String(d.brand || ''));
    req.setModel(String(d.model || ''));
    req.setPowerConsumptionWatts(Number(d.power_consumption_watts ?? d.consumption) || 0);
    req.setAcquisitionValue(Number(d.acquisition_value ?? d.price) || 0);
    const acquisitionDate = optionalIsoTimestamp(d.acquisition_date);
    if (acquisitionDate) {
        req.setAcquisitionDate(acquisitionDate);
    }
    req.setHourlyPrintingCost(Number(d.hourly_printing_cost) || 0);
    if (d.active != null) {
        req.setActive(Boolean(d.active));
    }
}

export function buildCreatePrinterRequest(data) {
    const { CreatePrinterRequest } = require('./proto/acompanha/printers/v1/printers_pb');
    const req = new CreatePrinterRequest();
    fillPrinterMutation(req, data);
    return req;
}

export function buildUpdatePrinterRequest(uuid, data) {
    const { UpdatePrinterRequest } = require('./proto/acompanha/printers/v1/printers_pb');
    const req = new UpdatePrinterRequest();
    req.setUuid(String(uuid));
    fillPrinterMutation(req, data);
    return req;
}

function fillFilamentMutation(req, data) {
    const d = data || {};
    req.setType(String(d.type || ''));
    req.setColor(String(d.color || ''));
    req.setColorLabel(String(d.color_label || ''));
    req.setBrand(String(d.brand || ''));
    req.setSpoolWeight(Number(d.spool_weight) || 0);
    req.setCurrentWeight(Number(d.current_weight) || 0);
    req.setCost(Number(d.cost ?? d.cost_per_kilogram) || 0);
}

export function buildCreateFilamentRequest(data) {
    const { CreateFilamentRequest } = require('./proto/acompanha/filaments/v1/filaments_pb');
    const req = new CreateFilamentRequest();
    fillFilamentMutation(req, data);
    return req;
}

export function buildUpdateFilamentRequest(uuid, data) {
    const { UpdateFilamentRequest } = require('./proto/acompanha/filaments/v1/filaments_pb');
    const req = new UpdateFilamentRequest();
    req.setUuid(String(uuid));
    fillFilamentMutation(req, data);
    return req;
}

export function buildCreateSalesChannelRequest(data) {
    const { CreateSalesChannelRequest } = require('./proto/acompanha/sales_channels/v1/sales_channels_pb');
    const req = new CreateSalesChannelRequest();
    fillSalesChannelMutation(req, data);
    return req;
}

export function buildUpdateSalesChannelRequest(uuid, data) {
    const { UpdateSalesChannelRequest } = require('./proto/acompanha/sales_channels/v1/sales_channels_pb');
    const req = new UpdateSalesChannelRequest();
    req.setUuid(String(uuid));
    fillSalesChannelMutation(req, data);
    return req;
}

function fillSalesChannelMutation(req, data) {
    const d = data || {};
    req.setName(String(d.name || ''));
    if (d.commission_percent != null && d.commission_percent !== '') {
        req.setCommissionPercent(Number(d.commission_percent));
    }
    if (d.fixed_fee != null && d.fixed_fee !== '') {
        req.setFixedFee(Number(d.fixed_fee));
    }
    if (d.payment_fee_percent != null && d.payment_fee_percent !== '') {
        req.setPaymentFeePercent(Number(d.payment_fee_percent));
    }
    if (d.min_fee != null && d.min_fee !== '') {
        req.setMinFee(Number(d.min_fee));
    }
    if (d.dispatch_days != null && d.dispatch_days !== '') {
        req.setDispatchDays(Number(d.dispatch_days));
    }
    if (d.notes) {
        req.setNotes(String(d.notes));
    }
}

export function buildCreatePaymentFeeRequest(data) {
    const { CreatePaymentFeeRequest } = require('./proto/acompanha/payment_fees/v1/payment_fees_pb');
    const req = new CreatePaymentFeeRequest();
    fillPaymentFeeMutation(req, data);
    return req;
}

export function buildUpdatePaymentFeeRequest(uuid, data) {
    const { UpdatePaymentFeeRequest } = require('./proto/acompanha/payment_fees/v1/payment_fees_pb');
    const req = new UpdatePaymentFeeRequest();
    req.setUuid(String(uuid));
    fillPaymentFeeMutation(req, data);
    return req;
}

function fillPaymentFeeMutation(req, data) {
    const d = data || {};
    req.setPaymentMethod(String(d.payment_method || ''));
    if (d.fee_percent != null && d.fee_percent !== '') {
        req.setFeePercent(Number(d.fee_percent));
    }
    req.setValidFrom(String(d.valid_from || ''));
    req.setValidTo(d.valid_to ? String(d.valid_to) : '');
    if (d.notes) {
        req.setNotes(String(d.notes));
    }
}

function fillProductMutation(req, data) {
    const d = data || {};
    req.setName(String(d.name || ''));
    req.setDescription(String(d.description || ''));
    const images = [];
    if (Array.isArray(d.images)) {
        d.images.forEach((img) => {
            if (img) {
                images.push(String(img));
            }
        });
    }
    if (d.image) {
        images.push(String(d.image));
    }
    if (images.length) {
        req.setImagesList(images);
    }
    if (d.cost_price != null) {
        req.setCostPrice(Number(d.cost_price));
    }
    const selling = d.selling_price ?? d.base_price;
    if (selling != null) {
        req.setSellingPrice(Number(selling));
    }
    if (d.base_price != null) {
        req.setBasePrice(Number(d.base_price));
    }
    const measuresJson = measuresToJson(d.measures ?? d.measurements);
    if (measuresJson) {
        req.setMeasures(measuresJson);
        req.setMeasurements(measuresJson);
    }
    const lead = d.fabrication_lead_time_days
        ?? d.availability?.lead_time_for_order;
    if (lead != null) {
        req.setFabricationLeadTimeDays(Number(lead));
    }
    if (d.is_customizable != null) {
        req.setIsCustomizable(Boolean(d.is_customizable));
    }
    if (d.assembly_required != null) {
        req.setAssemblyRequired(Boolean(d.assembly_required));
    }
    if (d.stock_quantity != null) {
        req.setStockQuantity(Number(d.stock_quantity));
    }
    if (d.total_sales_quantity != null) {
        req.setTotalSalesQuantity(Number(d.total_sales_quantity));
    }
}

export function buildCreateProductRequest(data) {
    const { CreateProductRequest } = require('./proto/acompanha/products/v1/products_pb');
    const req = new CreateProductRequest();
    fillProductMutation(req, data);
    return req;
}

export function buildUpdateProductRequest(uuid, data) {
    const { UpdateProductRequest } = require('./proto/acompanha/products/v1/products_pb');
    const req = new UpdateProductRequest();
    req.setUuid(String(uuid));
    fillProductMutation(req, data);
    return req;
}

export function buildAdjustProductStockRequest(uuid, data) {
    const { AdjustProductStockRequest } = require('./proto/acompanha/products/v1/products_pb');
    const req = new AdjustProductStockRequest();
    req.setUuid(String(uuid));
    req.setDelta(parseInt(data?.delta, 10) || 0);
    return req;
}

function fillCustomerMutation(req, data) {
    const d = data || {};
    req.setName(String(d.name || ''));
    if (d.phone != null) {
        req.setPhone(String(d.phone));
    }
    if (d.email != null) {
        req.setEmail(String(d.email));
    }
    const doc = d.document ?? d.document_number;
    if (doc != null) {
        req.setDocument(String(doc));
        req.setDocumentNumber(String(doc));
    }
    if (d.address != null) {
        req.setStreet(String(d.address));
    }
    if (d.address_number != null) {
        req.setNumber(String(d.address_number));
    }
    if (d.address_complement != null) {
        req.setComplement(String(d.address_complement));
    }
    if (d.neighborhood != null) {
        req.setNeighborhood(String(d.neighborhood));
    }
    if (d.city != null) {
        req.setCity(String(d.city));
    }
    if (d.state != null) {
        req.setState(String(d.state));
    }
    if (d.zip_code != null) {
        req.setZipCode(String(d.zip_code));
    }
}

export function buildCreateCustomerRequest(data) {
    const { CreateCustomerRequest } = require('./proto/acompanha/customers/v1/customers_pb');
    const req = new CreateCustomerRequest();
    fillCustomerMutation(req, data);
    return req;
}

export function buildUpdateCustomerRequest(uuid, data) {
    const { UpdateCustomerRequest } = require('./proto/acompanha/customers/v1/customers_pb');
    const req = new UpdateCustomerRequest();
    req.setUuid(String(uuid));
    fillCustomerMutation(req, data);
    return req;
}

export function buildCheckoutMutationRequest(RequestClass, uuid, data) {
    const req = new RequestClass();
    if (uuid) {
        req.setUuid(String(uuid));
    }
    const customer = customerRefFromRest(data.customer);
    if (customer) {
        req.setCustomer(customer);
    }
    const shipping = shippingMethodRefFromRest(data.shipping_method);
    if (shipping) {
        req.setShippingMethod(shipping);
    }
    if (Array.isArray(data.products)) {
        req.setProductsList(data.products.map(checkoutProductFromRest).filter(Boolean));
    }
    if (data.amount != null) {
        req.setAmount(Number(data.amount));
    }
    const channel = salesChannelRefFromRest(data.sales_channel);
    if (channel) {
        req.setSalesChannel(channel);
    }
    if (data.finish_order != null) {
        req.setFinishOrder(Boolean(data.finish_order));
    }
    if (Array.isArray(data.payment_methods)) {
        req.setPaymentMethodsList(data.payment_methods.map(paymentMethodFromRest).filter(Boolean));
    }
    const details = paymentDetailsFromRest(data.payment_details);
    if (details) {
        req.setPaymentDetails(details);
    }
    return req;
}

export function buildCreateCheckoutRequest(data) {
    const { CreateCheckoutRequest } = require('./proto/acompanha/checkouts/v1/checkouts_pb');
    const req = buildCheckoutMutationRequest(CreateCheckoutRequest, null, data);
    const ts = optionalIsoTimestamp(data.created_at);
    if (ts) {
        req.setCreatedAt(ts);
    }
    return req;
}

export function buildUpdateCheckoutRequest(uuid, data) {
    const { UpdateCheckoutRequest } = require('./proto/acompanha/checkouts/v1/checkouts_pb');
    return buildCheckoutMutationRequest(UpdateCheckoutRequest, uuid, data);
}

function fillTransactionMutation(req, data) {
    const d = data || {};
    req.setType(String(d.type || ''));
    req.setAmount(Number(d.amount) || 0);
    req.setNetAmount(Number(d.net_amount) || 0);
    req.setDeduction(Number(d.deduction) || 0);
    req.setDescription(String(d.description || ''));
    req.setPaymentMethod(String(d.payment_method || ''));
    req.setOrderId(String(d.order_id || ''));
    if (typeof req.setAuthorizationId === 'function') {
        req.setAuthorizationId(String(d.authorization_id || ''));
    }
    if (typeof req.setSource === 'function') {
        req.setSource(String(d.source || ''));
    }
    if (typeof req.setOutcomeCategory === 'function') {
        req.setOutcomeCategory(String(d.outcome_category || ''));
    }
}

export function buildCreateTransactionRequest(data) {
    const { CreateTransactionRequest } = require('./proto/acompanha/transactions/v1/transactions_pb');
    const req = new CreateTransactionRequest();
    fillTransactionMutation(req, data);
    return req;
}

export function buildUpdateTransactionRequest(uuid, data) {
    const { UpdateTransactionRequest } = require('./proto/acompanha/transactions/v1/transactions_pb');
    const req = new UpdateTransactionRequest();
    req.setUuid(String(uuid));
    fillTransactionMutation(req, data);
    return req;
}

function fillMaintenanceMutation(req, data) {
    const d = data || {};
    req.setPrinterUuid(String(d.printer_uuid || ''));
    req.setType(String(d.type || ''));
    req.setProblemDescription(String(d.problem_description || ''));
    const occurred = optionalIsoTimestamp(d.occurred_at);
    if (occurred) {
        req.setOccurredAt(occurred);
    }
    req.setAnalysisDurationMinutes(Number(d.analysis_duration_minutes) || 0);
    req.setPerformedActions(String(d.performed_actions || ''));
    req.setPrinterHoursCount(Number(d.printer_hours_count) || 0);
    req.setReplacementPartsList(replacementPartsFromRest(d.replacement_parts));
    req.setCorrectionDurationMinutes(Number(d.correction_duration_minutes) || 0);
}

export function buildCreateMaintenanceRequest(data) {
    const { CreateMaintenanceRequest } = require('./proto/acompanha/printer_maintenances/v1/printer_maintenances_pb');
    const req = new CreateMaintenanceRequest();
    fillMaintenanceMutation(req, data);
    return req;
}

export function buildUpdateMaintenanceRequest(uuid, data) {
    const { UpdateMaintenanceRequest } = require('./proto/acompanha/printer_maintenances/v1/printer_maintenances_pb');
    const req = new UpdateMaintenanceRequest();
    req.setUuid(String(uuid));
    fillMaintenanceMutation(req, data);
    return req;
}

export function buildListMaintenancesRequest(query) {
    const { ListMaintenancesRequest } = require('./proto/acompanha/printer_maintenances/v1/printer_maintenances_pb');
    const req = new ListMaintenancesRequest();
    req.setPagination(buildPaginationRequest(query));
    if (query.printer_uuid) {
        req.setPrinterUuid(String(query.printer_uuid));
    }
    if (query.type) {
        req.setType(String(query.type));
    }
    return req;
}

export function buildListOrdersRequest(query) {
    const { ListOrdersRequest } = require('./proto/acompanha/orders/v1/orders_pb');
    const req = new ListOrdersRequest();
    req.setPagination(buildPaginationRequest(query));
    const from = optionalIsoTimestamp(query.from);
    const to = optionalIsoTimestamp(query.to);
    if (from) {
        req.setFrom(from);
    }
    if (to) {
        req.setTo(to);
    }
    if (query.sales_channel_id) {
        req.setSalesChannelId(String(query.sales_channel_id));
    }
    return req;
}

export function buildListTransactionsRequest(query) {
    const { ListTransactionsRequest } = require('./proto/acompanha/transactions/v1/transactions_pb');
    const req = new ListTransactionsRequest();
    req.setPagination(buildPaginationRequest(query));
    const from = optionalIsoTimestamp(query.from);
    const to = optionalIsoTimestamp(query.to);
    if (from) {
        req.setFrom(from);
    }
    if (to) {
        req.setTo(to);
    }
    return req;
}

export function buildProductsSummaryRequest(query) {
    const { GetProductsSummaryRequest } = require('./proto/acompanha/products/v1/products_pb');
    const req = new GetProductsSummaryRequest();
    if (query.q) {
        req.setQ(String(query.q));
    }
    return req;
}

export function buildOrdersSummaryRequest(query) {
    const { GetOrdersSummaryRequest } = require('./proto/acompanha/orders/v1/orders_pb');
    const req = new GetOrdersSummaryRequest();
    if (query.q) {
        req.setQ(String(query.q));
    }
    const from = optionalIsoTimestamp(query.from);
    const to = optionalIsoTimestamp(query.to);
    if (from) {
        req.setFrom(from);
    }
    if (to) {
        req.setTo(to);
    }
    if (query.sales_channel_id) {
        req.setSalesChannelId(String(query.sales_channel_id));
    }
    return req;
}
